import React, { useState, useEffect } from "react";
import { ThermalModalHeader, ThermalModalFooter } from "../components/common/ThermalDesignerCommonComponents";
import { useThermalDesign, DEFAULT_COATING_CONFIG } from "../../../ThermalDesignContext";
import { useModalAutoClose } from "../hooks/ThermalDesignerHooks";
import { DividerLine, NoCoatingIcon, MLICoatingIcon, OSRCoatingIcon, FillAllIcon, ClearAllIcon } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import type { CoatingCell } from "../../../ThermalDesignTypes";
import CustomToolTip from "../../../../../../CommonComponents/CustomToolTip";
import "../styles/ConfigureCoatingModal.scss";

const CELL_ICON: Record<CoatingCell, React.FC> = {
  none: NoCoatingIcon,
  mli: MLICoatingIcon,
  osr: OSRCoatingIcon,
};

const TOTAL_CELLS = 9;

const calcPercentages = (cells: CoatingCell[]) => {
  const counts = { none: 0, mli: 0, osr: 0 };
  cells.forEach((c) => counts[c]++);
  return {
    none: parseFloat(((counts.none / TOTAL_CELLS) * 100).toFixed(2)),
    mli: parseFloat(((counts.mli / TOTAL_CELLS) * 100).toFixed(2)),
    osr: parseFloat(((counts.osr / TOTAL_CELLS) * 100).toFixed(2)),
  };
};

interface ConfigureCoatingModalProps {
  onClose: () => void;
  cells: CoatingCell[];
  setCells: React.Dispatch<React.SetStateAction<CoatingCell[]>>;
}

const ConfigureCoatingModal: React.FC<ConfigureCoatingModalProps> = ({ onClose, cells, setCells }) => {
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const { nodeSpecsOptions, setNodeSpecsOptions, isSatDesignerSidebarOpen, openModal } = useThermalDesign();
  useModalAutoClose("isConfigureCoatingModalOpen", ["isCoatingSettingsModalOpen"]);

  const selectedNode = nodeSpecsOptions.find((n) => n.isSelected);
  const activeBrush = openModal.activeBrush || "none";

  const handleFillAll = () => {
    setCells(Array(TOTAL_CELLS).fill(activeBrush) as CoatingCell[]);
  };

  const handleClearAll = () => {
    setCells(Array(TOTAL_CELLS).fill("none") as CoatingCell[]);
  };

  const handleCellClick = (i: number) => {
    setCells((prev) => {
      const next = [...prev];
      next[i] = activeBrush;
      return next;
    });
  };

  const handleCellRightClick = (e: React.MouseEvent, i: number) => {
    e.preventDefault();
    setCells((prev) => {
      const next = [...prev];
      next[i] = "none";
      return next;
    });
  };

  const handleSave = () => {
    if (!selectedNode?.values) {
      onClose();
      return;
    }
    const pcts = calcPercentages(cells);
    const prevCoating = selectedNode.values.coating || DEFAULT_COATING_CONFIG;
    setNodeSpecsOptions((prev) =>
      prev.map((node) => {
        if (node.id !== selectedNode.id || !node.values) return node;
        return {
          ...node,
          values: {
            ...node.values,
            coating: {
              ...prevCoating,
              none: { ...prevCoating.none, percentage: pcts.none },
              mli: { ...prevCoating.mli, percentage: pcts.mli },
              osr: { ...prevCoating.osr, percentage: pcts.osr },
              cells,
            },
          },
        };
      }),
    );
    onClose();
  };

  const percentages = calcPercentages(cells);

  return (
    <div className={`nodeSpecsModal customMaterialsModal configure-coating-modal ${!isSatDesignerSidebarOpen ? "left" : "right"}`}>
      <ThermalModalHeader
        header={{
          title: "Configure Coating",
          isModalMinimized,
          setIsModalMinimized,
          onClose,
        }}
      />
      {!isModalMinimized && (
        <>
          <div className="configure-coating-summary">
            <span className="configure-coating-summary-item">None: <strong>{percentages.none}%</strong></span>
            <span className="configure-coating-summary-item">MLI: <strong>{percentages.mli}%</strong></span>
            <span className="configure-coating-summary-item">OSR: <strong>{percentages.osr}%</strong></span>
          </div>
          <div className="configure-coating-active-brush">
            <span className="configure-coating-brush-label">{activeBrush.toUpperCase()}</span>
            <span className="configure-coating-brush-hint">&nbsp;(right-click to reset to None)</span>
            <div className="configure-coating-actions">
              <CustomToolTip title="Fill All" placement="top">
                <button className="fill-all-btn" type="button" aria-label="Fill All" onClick={handleFillAll}>
                  <FillAllIcon />
                </button>
              </CustomToolTip>
              <CustomToolTip title="Clear All" placement="top">
                <button className="clear-all-btn" type="button" aria-label="Clear All" onClick={handleClearAll}>
                  <ClearAllIcon />
                </button>
              </CustomToolTip>
            </div>
          </div>
          <div className="configure-coating-grid">
            {cells.map((cell, i) => {
              const Icon = CELL_ICON[cell];
              return (
                <button
                  key={i}
                  type="button"
                  className="configure-coating-cell"
                  onClick={() => handleCellClick(i)}
                  onContextMenu={(e) => handleCellRightClick(e, i)}
                >
                  <Icon />
                  <span className="configure-coating-cell-id">{i + 1}</span>
                </button>
              );
            })}
          </div>
          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>
          <ThermalModalFooter onSave={handleSave} isLastNode onCancel={onClose} saveLabel="Save & Exit" />
        </>
      )}
    </div>
  );
};

export default ConfigureCoatingModal;
